/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The image-position format ("<x>% <y>%", '' = centre) and where a position
 * puts an `object-fit: cover` frame inside its image — plain ESM JavaScript
 * on purpose (the readingTimeCore.js pattern), because it has consumers that
 * cannot share TypeScript:
 *   1. the client and the server routes, via the typed facade
 *      src/lib/imageFocus.ts (whose header explains the format);
 *   2. src/lib/shareImage.js, which puts an article hero's position into the
 *      address of its share crop — called by scripts/prerender.mjs, plain
 *      Node, as well as by the server head and the page;
 *   3. scripts/imageVariantWorker.mjs, which cuts the share crop around it.
 * Keep it dependency-free and DOM-free.
 */

/** @typedef {{ x: number, y: number }} FocusPoint */

/** @type {Readonly<FocusPoint>} */
export const CENTER_FOCUS = Object.freeze({ x: 50, y: 50 });

/** Canonical stored form: two integer percentages, or '' for unset. */
const FOCUS_RE = /^(?:100|\d{1,2})% (?:100|\d{1,2})%$/;
/** What a tolerant read accepts: two percentages, any spacing, decimals and signs. */
const LOOSE_FOCUS_RE = /^\s*(-?[\d.]+)%\s+(-?[\d.]+)%\s*$/;

/**
 * @param {unknown} value
 * @returns {boolean}
 */
export function isValidFocus(value) {
  if (typeof value !== 'string') return false;
  if (value === '') return true;
  return FOCUS_RE.test(value);
}

/**
 * A percentage clamped to 0–100 and rounded; anything not finite is centre.
 *
 * @param {number} n
 * @returns {number}
 */
export function clampFocusPct(n) {
  if (!Number.isFinite(n)) return 50;
  return Math.min(100, Math.max(0, Math.round(n)));
}

/**
 * Tolerant read for rendering. Anything unparseable falls back to centre.
 *
 * @param {unknown} value
 * @returns {FocusPoint}
 */
export function parseFocus(value) {
  if (typeof value !== 'string' || value === '') return { ...CENTER_FOCUS };
  const m = LOOSE_FOCUS_RE.exec(value);
  if (!m) return { ...CENTER_FOCUS };
  return { x: clampFocusPct(parseFloat(m[1])), y: clampFocusPct(parseFloat(m[2])) };
}

/**
 * Canonical string for a point. Dead centre serialises to '' — "explicitly
 * centred" and "never set" render identically.
 *
 * @param {FocusPoint} point
 * @returns {string}
 */
export function formatFocus(point) {
  const x = clampFocusPct(point.x);
  const y = clampFocusPct(point.y);
  if (x === 50 && y === 50) return '';
  return `${x}% ${y}%`;
}

/**
 * Best-effort canonicalisation of an incoming value. A value that cannot be
 * parsed is handed back trimmed rather than coerced to '' — the caller
 * refuses it through isValidFocus (see src/lib/imageFocus.ts for why).
 *
 * @param {unknown} value
 * @returns {string}
 */
export function normalizeFocus(value) {
  if (typeof value !== 'string') return '';
  const raw = value.trim();
  if (raw === '') return '';
  const m = LOOSE_FOCUS_RE.exec(raw);
  if (!m) return raw;
  return formatFocus({ x: parseFloat(m[1]), y: parseFloat(m[2]) });
}

/**
 * The part of a `sourceWidth` × `sourceHeight` image that a
 * `frameWidth` × `frameHeight` frame shows with `object-fit: cover` and
 * `object-position: <x>% <y>%` — in source pixels, ready for an extract
 * before the resize to the frame's size.
 *
 * Cover keeps the full extent of the axis the frame is relatively shorter on
 * and cuts the other one to the frame's aspect. object-position x% puts x% of
 * what is cut off on the left (and y% of it on the top): 0 % is the left (top)
 * edge, 100 % the right (bottom) edge, 50 % the centre. The window therefore
 * never leaves the image, whatever the position — the edges clamp it — and
 * the axis that is not cut ignores its percentage.
 *
 * @param {number} sourceWidth   Pixels, after EXIF orientation.
 * @param {number} sourceHeight
 * @param {number} frameWidth
 * @param {number} frameHeight
 * @param {FocusPoint} focus     Percentages; clamped to 0–100.
 * @returns {{ left: number, top: number, width: number, height: number }}
 */
export function coverCropWindow(sourceWidth, sourceHeight, frameWidth, frameHeight, focus) {
  const width = Math.max(
    1,
    Math.min(sourceWidth, Math.round((sourceHeight * frameWidth) / frameHeight))
  );
  const height = Math.max(
    1,
    Math.min(sourceHeight, Math.round((sourceWidth * frameHeight) / frameWidth))
  );
  const x = clampFocusPct(focus?.x);
  const y = clampFocusPct(focus?.y);
  return {
    left: Math.round(((sourceWidth - width) * x) / 100),
    top: Math.round(((sourceHeight - height) * y) / 100),
    width,
    height,
  };
}
